import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Smartphone, DollarSign, AlertCircle, List, Activity, CreditCard, Globe, RefreshCw, Zap, Shield, CheckCircle, ArrowRight } from 'react-feather';
import './Deposit.css';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { depositsAPI } from '../api';
import { formatCurrencyWithConversion, convertToUsd, convertCurrency, getCurrencyInfo, formatCurrency } from '../utils/currency';
import { SWYCHR_MARKETS, getSwychrMarketByCountry } from '../utils/swychrMarkets';

const FALLBACK_XAF_PER_USD = 655.957;

const DEFAULT_SWYCHR_MARKET_CODE = 'CM:XAF';
const ZERO_DECIMAL_CURRENCIES = new Set(['XAF', 'XOF', 'CDF', 'GNF', 'RWF', 'TZS', 'UGX', 'JPY']);
const FAPSHI_PENDING_DEPOSIT_STORAGE_KEY = 'nb_fapshi_pending_deposit_id';
const MONETBIL_PENDING_DEPOSIT_STORAGE_KEY = 'nb_monetbil_pending_deposit_id';
const SWYCHR_PENDING_DEPOSIT_STORAGE_KEY = 'nb_swychr_pending_deposit_id';
const NOTCHPAY_PENDING_DEPOSIT_STORAGE_KEY = 'nb_notchpay_pending_deposit_id';
const SWYCHR_VERIFY_RETRY_COUNT = 6;
const SWYCHR_VERIFY_RETRY_DELAY_MS = 5000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const STATUS_BADGE = {
  pending: { label: 'Pending', color: '#eab308', bg: 'rgba(234,179,8,0.1)' },
  completed: { label: 'Completed', color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
  failed: { label: 'Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' }
};

const GATEWAY_INFO = {
  fapshi: {
    depositSteps: [
      'Enter your deposit amount and click Pay to go to checkout.',
      'On the checkout page enter your Mobile Money number and click Deposit.',
      'Approve the push on your MTN MoMo or Orange Money app — your balance is credited within seconds.'
    ]
  },
  notchpay: {
    depositSteps: [
      'Enter your deposit amount and continue to Mobile Money Africa 2 secure checkout.',
      'Select your payment method (MTN Mobile Money, Orange Money, or Card) and complete payment.',
      'After payment is confirmed, your NiceBooster balance is credited immediately.'
    ]
  },
  monetbil: {
    depositSteps: [
      'Enter your deposit amount and continue to Monetbil checkout.',
      'Choose a supported mobile money option and complete the payment.',
      'After Monetbil confirms the payment, your NiceBooster balance is credited automatically.'
    ]
  },
  swychr: {
    depositSteps: [
      'Select your country and enter your deposit amount in the local currency.',
      'Enter your mobile number to receive an instant USSD authorization push directly on your phone.',
      'Approve with your Mobile Money PIN — your NiceBooster balance is credited in real time.'
    ]
  },
  swychr_crypto: {
    depositSteps: [
      'Enter your deposit amount in USD and select your network (TRON TRC-20, BSC BEP-20, or Ethereum ERC-20).',
      'Generate your secure 60-minute Swychr Crypto checkout link.',
      'Send USDT to the address shown. As soon as confirmed on-chain, your balance is credited automatically.'
    ]
  },
  helaket: {
    depositSteps: [
      'Enter your amount in USD and choose the crypto you want to pay with.',
      'Complete the payment on the crypto invoice page.',
      'After the payment is confirmed, your NiceBooster balance is credited automatically.'
    ]
  }
};

const GATEWAYS = [
  {
    id: 'fapshi',
    name: 'Mobile Money',
    sub: 'MTN / Orange · Cameroon',
    badge: 'Instant USSD',
    badgeColor: '#f97316',
    icon: <Smartphone size={22} />,
    color: '#f97316',
    desc: 'MTN Mobile Money or Orange Money. Instant push straight to your phone in FCFA.'
  },
  {
    id: 'notchpay',
    name: 'Mobile Money Africa 2',
    sub: 'MTN · Orange · Cards',
    badge: 'Popular',
    badgeColor: '#10b981',
    icon: <CreditCard size={22} />,
    color: '#6366f1',
    desc: 'Pay with MTN MoMo, Orange Money, or Credit/Debit Cards across Africa.'
  },
  {
    id: 'swychr',
    name: 'Mobile Money Africa',
    sub: 'Africa · 15+ Countries',
    badge: '15+ Countries',
    badgeColor: '#0ea5e9',
    icon: <Globe size={22} />,
    color: '#0ea5e9',
    desc: 'Instant direct USSD push in Cameroon and 15+ African countries.'
  },
  {
    id: 'swychr_crypto',
    name: 'Crypto (USDT)',
    sub: 'TRON · BSC · Ethereum',
    badge: 'Zero Extra Fee',
    badgeColor: '#10b981',
    icon: <DollarSign size={22} />,
    color: '#10b981',
    desc: 'Pay with USDT via TRON (TRC-20), BSC (BEP-20), or Ethereum.'
  },
  {
    id: 'monetbil',
    name: 'Monetbil',
    sub: 'Mobile Money · FCFA',
    badge: null,
    badgeColor: '#8b5cf6',
    icon: <CreditCard size={22} />,
    color: '#8b5cf6',
    desc: 'Mobile Money in Cameroon, Senegal and across Africa. FCFA or local currency.'
  },
  {
    id: 'helaket',
    name: 'Helaket Crypto',
    sub: 'Cryptocurrency · USD',
    badge: 'Multi-Coin',
    badgeColor: '#f59e0b',
    icon: <DollarSign size={22} />,
    color: '#f59e0b',
    desc: 'Pay with USDT, Bitcoin, Ethereum, or Litecoin.'
  }
];

const GATEWAY_VERIFY_CONFIG = {
  fapshi: {
    verify: depositsAPI.verifyFapshi,
    storageKey: FAPSHI_PENDING_DEPOSIT_STORAGE_KEY,
    completedMessage: 'Mobile Money payment confirmed and your balance has been credited.',
    pendingMessage: 'Mobile Money payment received. Verification is in progress.',
    fallbackMessage: 'Mobile Money payment completed. Your balance will update automatically once confirmed.',
  },
  monetbil: {
    verify: depositsAPI.verifyMonetbil,
    storageKey: MONETBIL_PENDING_DEPOSIT_STORAGE_KEY,
    completedMessage: 'Monetbil payment confirmed and your balance has been credited.',
    pendingMessage: 'Monetbil payment received. Verification is in progress.',
    fallbackMessage: 'Monetbil payment completed. Your balance will update automatically once confirmed.',
  },
  swychr: {
    verify: depositsAPI.verifySwychr,
    storageKey: SWYCHR_PENDING_DEPOSIT_STORAGE_KEY,
    completedMessage: 'Mobile Money Africa payment confirmed and your balance has been credited.',
    pendingMessage: 'Mobile Money Africa payment received. Verification is in progress.',
    fallbackMessage: 'Mobile Money Africa payment completed. Verification is still pending.',
  },
  swychr_crypto: {
    verify: depositsAPI.verifySwychr,
    storageKey: 'nb_swychr_crypto_pending_deposit_id',
    completedMessage: 'Crypto payment confirmed and your balance has been credited.',
    pendingMessage: 'Crypto payment received. Verification is in progress.',
    fallbackMessage: 'Crypto payment completed. Verification is still pending.',
  },
  notchpay: {
    verify: depositsAPI.verifyNotchpay,
    storageKey: NOTCHPAY_PENDING_DEPOSIT_STORAGE_KEY,
    completedMessage: 'Mobile Money Africa 2 payment confirmed and your balance has been credited.',
    pendingMessage: 'Mobile Money Africa 2 payment received. Verification is in progress.',
    fallbackMessage: 'Mobile Money Africa 2 payment completed. Verification is in progress.',
  },
};

export default function Deposit() {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [gateway, setGateway] = useState('fapshi');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USDT');
  const [cryptoChain, setCryptoChain] = useState('TRON');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [historyView, setHistoryView] = useState('recent');
  const [rateInfo, setRateInfo] = useState(null);
  const [depositFeatures, setDepositFeatures] = useState(null);
  const [swychrMarketCode, setSwychrMarketCode] = useState(DEFAULT_SWYCHR_MARKET_CODE);
  const [swychrPhone, setSwychrPhone] = useState('');
  const [verifyingId, setVerifyingId] = useState(null);
  const autoCheckedSwychrRef = useRef(false);
  const [fapshiPhone, setFapshiPhone] = useState('');
  const [fapshiPendingId, setFapshiPendingId] = useState(null);
  const [fapshiPollStatus, setFapshiPollStatus] = useState(null); // 'pending' | 'completed' | 'failed'
  const fapshiPollRef = useRef(null);

  useEffect(() => {
    if (user?.phone && !swychrPhone) {
      setSwychrPhone(user.phone);
    }
  }, [user?.phone, swychrPhone]);

  useEffect(() => {
    depositsAPI.history()
      .then((r) => setHistory(r.data.deposits || []))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, []);

  useEffect(() => {
    depositsAPI.rateInfo()
      .then((r) => {
        setRateInfo(r.data.rate || null);
        setDepositFeatures({
          bonuses: r.data.bonuses || null,
          gateways: r.data.gateways || null,
          swychrMarkets: r.data.swychr_markets || [],
        });
      })
      .catch(() => {
        setRateInfo(null);
        setDepositFeatures(null);
      });
  }, []);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const fapshiStatus = searchParams.get('status');
    const gatewayParam = paymentStatus
      ? searchParams.get('gateway')
      : (fapshiStatus === 'SUCCESSFUL' && searchParams.get('transId') ? 'fapshi' : '');
    const depositIdParam = searchParams.get('deposit_id');
    const normalizedPaymentStatus = paymentStatus || (gatewayParam === 'fapshi' && fapshiStatus === 'SUCCESSFUL' ? 'success' : '');

    if (!normalizedPaymentStatus) return;

    if (normalizedPaymentStatus === 'success') {
      setError('');

      const handleSuccessReturn = async () => {
        try {
          const verifyConfig = GATEWAY_VERIFY_CONFIG[gatewayParam];
          if (verifyConfig) {
            const storedDepositId = sessionStorage.getItem(verifyConfig.storageKey);
            const gatewayDepositId = depositIdParam || storedDepositId;

            if (!gatewayDepositId) {
              setSuccess(verifyConfig.fallbackMessage);
              return;
            }

            let lastVerifyMessage = verifyConfig.pendingMessage;
            let verifyCompleted = false;

            for (let attempt = 0; attempt < SWYCHR_VERIFY_RETRY_COUNT; attempt += 1) {
              const verifyRes = await verifyConfig.verify(gatewayDepositId);
              const verifyMessage = verifyRes.data?.message || verifyConfig.pendingMessage;
              const verifyStatus = verifyRes.data?.status || 'pending';

              lastVerifyMessage = verifyMessage;

              if (verifyStatus === 'completed') {
                verifyCompleted = true;
                break;
              }

              if (attempt < SWYCHR_VERIFY_RETRY_COUNT - 1) {
                setSuccess(`Payment is still pending. Rechecking in ${Math.ceil(SWYCHR_VERIFY_RETRY_DELAY_MS / 1000)} seconds...`);
                await delay(SWYCHR_VERIFY_RETRY_DELAY_MS);
              }
            }

            if (verifyCompleted) {
              setSuccess(verifyConfig.completedMessage);
              sessionStorage.removeItem(verifyConfig.storageKey);
            } else {
              setSuccess(lastVerifyMessage);
            }
          } else {
            setSuccess(`${gatewayParam ? `${gatewayParam} payment` : 'Payment'} completed. Your balance will update automatically once confirmed.`);
          }
        } catch (err) {
          const verifyError = err.response?.data?.error || err.message;
          setSuccess(`${gatewayParam ? `${gatewayParam} payment` : 'Payment'} completed. Verification is still pending.${verifyError ? ` ${verifyError}` : ''}`);
        } finally {
          refreshUser();
          depositsAPI.history().then((r) => setHistory(r.data.deposits || []));
        }
      };

      handleSuccessReturn();
    } else if (normalizedPaymentStatus === 'cancelled') {
      if (gatewayParam && GATEWAY_VERIFY_CONFIG[gatewayParam]) {
        sessionStorage.removeItem(GATEWAY_VERIFY_CONFIG[gatewayParam].storageKey);
      }
      setError(`${gatewayParam ? `${gatewayParam} payment` : 'Payment'} was cancelled. You can try again anytime.`);
      setSuccess('');
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('payment');
    nextParams.delete('gateway');
    nextParams.delete('deposit_id');
    nextParams.delete('status');
    nextParams.delete('transId');
    nextParams.delete('payment_status');
    nextParams.delete('payment_ref');
    setSearchParams(nextParams, { replace: true });
  }, [refreshUser, searchParams, setSearchParams]);

  useEffect(() => {
    const hasReturnParams =
      searchParams.has('payment') ||
      searchParams.has('gateway') ||
      searchParams.has('deposit_id') ||
      searchParams.has('status') ||
      searchParams.has('transId');

    if (hasReturnParams || autoCheckedSwychrRef.current) return;

    const pendingDepositId = sessionStorage.getItem(SWYCHR_PENDING_DEPOSIT_STORAGE_KEY);
    if (!pendingDepositId) return;

    autoCheckedSwychrRef.current = true;

    const verifyPendingSwychrDeposit = async () => {
      try {
        const res = await depositsAPI.verifySwychr(pendingDepositId);
        if (res.data?.status === 'completed') {
          setSuccess('Swychr payment confirmed and your balance has been credited.');
          setError('');
          sessionStorage.removeItem(SWYCHR_PENDING_DEPOSIT_STORAGE_KEY);
        }
      } catch (err) {
        // Keep this silent on normal page load; the manual Verify button remains available.
      } finally {
        refreshUser();
        depositsAPI.history().then((r) => setHistory(r.data.deposits || []));
      }
    };

    verifyPendingSwychrDeposit();
  }, [refreshUser, searchParams]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fapshi Direct Pay polling — runs in DepositCheckout instead
  // (kept as no-op to avoid breaking state refs used during checkout navigation)

  const switchGateway = (gw) => {
    setGateway(gw);
    setAmount('');
    setError('');
    setSuccess('');
    setFapshiPendingId(null);
    setFapshiPollStatus(null);
  };

  const isFapshi = gateway === 'fapshi';
  const isMonetbil = gateway === 'monetbil';
  const isSwychr = gateway === 'swychr';
  const isNotchpay = gateway === 'notchpay';
  const isSwychrCrypto = gateway === 'swychr_crypto';
  const isHelaket = gateway === 'helaket';
  const isMultiCountry = isSwychr || isNotchpay;
  const isMobileMoneyGateway = isFapshi || isMonetbil || isSwychr || isNotchpay;
  const isCryptoGateway = isHelaket || isSwychrCrypto;
  const gatewayEnabledMap = depositFeatures?.gateways || { fapshi: true, notchpay: true, monetbil: false, helaket: false, swychr: false, swychr_crypto: true };
  const availableGateways = GATEWAYS.filter((gw) => gatewayEnabledMap[gw.id] !== false);
  const swychrMarkets = SWYCHR_MARKETS;
  const amountNum = parseFloat(amount) || 0;
  const userCurrency = user?.currency || 'USD';
  const balanceDisplay = formatCurrencyWithConversion(user?.balance || 0, user?.currency || 'USD');
  const gatewayInfo = GATEWAY_INFO[gateway] || GATEWAY_INFO.fapshi;
  const effectiveXafPerUsd = rateInfo?.effectiveRate || FALLBACK_XAF_PER_USD;
  const selectedSwychrMarket = swychrMarkets.find(
    (market) => `${market.countryCode}:${market.currency}` === swychrMarketCode
  ) || swychrMarkets[0] || null;
  const inputCurrency = isMultiCountry ? (selectedSwychrMarket?.currency || 'XAF') : userCurrency;
  const inputCurrencyInfo = getCurrencyInfo(inputCurrency);
  const inputDecimals = ZERO_DECIMAL_CURRENCIES.has(inputCurrency) ? 0 : 2;
  const accountAmountUsd = isSwychr
    ? convertToUsd(amountNum, inputCurrency)
    : convertToUsd(amountNum, userCurrency);
  const mobileGatewayAmountXaf = Math.round(accountAmountUsd * effectiveXafPerUsd);
  const swychrLocalDecimals = ZERO_DECIMAL_CURRENCIES.has(selectedSwychrMarket?.currency || '') ? 0 : 2;
  const swychrLocalAmount = amountNum;
  const minDepositConfig = depositFeatures?.min_deposit;
  const isMinDepositEnforced = Boolean(minDepositConfig && minDepositConfig.enabled === true);
  const configuredMinUsd = Number(minDepositConfig?.amount_usd) || 0;
  const configuredMinXaf = minDepositConfig?.amount_xaf ? Number(minDepositConfig.amount_xaf) : null;
  const cryptoMinUsd = Number(minDepositConfig?.crypto_min_deposit_usd) || 0;
  const isCryptoMinEnforced = isSwychrCrypto && cryptoMinUsd > 0;
  const minimumDepositUsd = isCryptoMinEnforced
    ? cryptoMinUsd
    : isMinDepositEnforced
    ? (configuredMinXaf ? configuredMinXaf / effectiveXafPerUsd : configuredMinUsd)
    : 0.1;
  const minimumDepositInInputCurrency = isCryptoMinEnforced
    ? convertCurrency(cryptoMinUsd, inputCurrency)
    : (isMinDepositEnforced && inputCurrency === 'XAF' && configuredMinXaf)
    ? configuredMinXaf
    : convertCurrency(minimumDepositUsd, inputCurrency);
  const quickChipDecimals = inputDecimals;
  const estimatedCreditLabel = amountNum > 0
    ? `Estimated credit: ${formatCurrencyWithConversion(accountAmountUsd, userCurrency, ZERO_DECIMAL_CURRENCIES.has(userCurrency) ? 0 : 2)}`
    : '';
  const swychrChargeLabel = isMultiCountry && selectedSwychrMarket && amountNum > 0
    ? `Paying in ${selectedSwychrMarket.label}: ${formatCurrency(swychrLocalAmount, selectedSwychrMarket.currency, swychrLocalDecimals)}`
    : '';
  const minimumDepositLabel = isCryptoMinEnforced
    ? `$${cryptoMinUsd.toFixed(2)} USD minimum`
    : isMinDepositEnforced
    ? (isMultiCountry
        ? `${formatCurrency(minimumDepositInInputCurrency, inputCurrency, quickChipDecimals)} minimum`
        : `${formatCurrencyWithConversion(minimumDepositUsd, userCurrency, quickChipDecimals)} minimum`)
    : '';
  const firstDepositBonus = depositFeatures?.bonuses;

  useEffect(() => {
    if (!availableGateways.length) return;
    if (!availableGateways.some((gw) => gw.id === gateway)) {
      setGateway(availableGateways[0].id);
    }
  }, [availableGateways, gateway]);

  useEffect(() => {
    if (!swychrMarkets.length) return;
    if (!swychrMarkets.some((market) => `${market.countryCode}:${market.currency}` === swychrMarketCode)) {
      setSwychrMarketCode(`${swychrMarkets[0].countryCode}:${swychrMarkets[0].currency}`);
    }
  }, [swychrMarketCode, swychrMarkets]);

  useEffect(() => {
    if (!swychrMarkets.length) return;
    const preferredMarket = getSwychrMarketByCountry(user?.country_code);
    if (!preferredMarket) return;
    const preferredMarketCode = `${preferredMarket.countryCode}:${preferredMarket.currency}`;
    if (swychrMarkets.some((market) => `${market.countryCode}:${market.currency}` === preferredMarketCode)) {
      setSwychrMarketCode(preferredMarketCode);
    }
  }, [swychrMarkets, user?.country_code]);

  const previewLine = amountNum >= minimumDepositInInputCurrency
    ? [estimatedCreditLabel, swychrChargeLabel].filter(Boolean).join(' | ')
    : '';
  const visibleHistory = historyView === 'all' ? history : history.slice(0, 5);

  const renderHistoryCard = () => (
    <div className="wallet-card">
      <div className="wallet-history-header">
        <h3 className="wallet-section-title" style={{ margin: 0 }}>
          <List size={18} style={{ color: 'var(--accent)' }} /> Recent Deposits
        </h3>
        {history.length > 5 && (
          <select
            className="form-input"
            value={historyView}
            onChange={(e) => setHistoryView(e.target.value)}
            style={{ width: 110, padding: '6px 10px', fontSize: 12, minHeight: 'auto', borderRadius: 8 }}
          >
            <option value="recent">Last 5</option>
            <option value="all">View all ({history.length})</option>
          </select>
        )}
      </div>

      {histLoading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '36px 0', color: 'var(--text-muted)' }}>
          <AlertCircle size={38} style={{ color: 'var(--text-muted)', marginBottom: 8, opacity: 0.6 }} />
          <p style={{ fontSize: 14, margin: 0 }}>No deposit transactions yet</p>
        </div>
      ) : (
        <div className="wallet-history-list">
          {visibleHistory.map((deposit) => {
            const meta = STATUS_BADGE[deposit.status] || STATUS_BADGE.pending;
            let localAmount = null;
            let localCurrency = '';
            try {
              const parsedMetadata = JSON.parse(deposit.metadata || '{}');
              localAmount = parsedMetadata.local_amount ?? deposit.xaf_amount ?? null;
              localCurrency = parsedMetadata.currency || (deposit.xaf_amount ? 'XAF' : '');
            } catch {
              localAmount = deposit.xaf_amount;
              localCurrency = deposit.xaf_amount ? 'XAF' : '';
            }

            const isCryptoGw = deposit.gateway === 'helaket' || deposit.gateway === 'swychr_crypto';
            const gwLabel = deposit.gateway === 'helaket' ? 'Crypto' : deposit.gateway === 'fapshi' ? 'Mobile Money' : deposit.gateway === 'notchpay' ? 'Mobile Money Africa 2' : deposit.gateway === 'swychr' ? 'Mobile Money Africa' : deposit.gateway === 'swychr_crypto' ? 'Crypto (USDT)' : deposit.gateway;

            return (
              <div key={deposit.id} className="wallet-history-item">
                <div className="wallet-history-item-left">
                  <div className="wallet-history-item-icon">
                    {isCryptoGw ? <DollarSign size={18} /> : <Smartphone size={18} />}
                  </div>
                  <div className="wallet-history-item-details">
                    <div className="wallet-history-item-amount">
                      {formatCurrencyWithConversion(deposit.amount, user?.currency || 'USD')}
                      {localAmount && localCurrency && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 6, fontWeight: 500 }}>
                          ({Number(localAmount).toLocaleString()} {localCurrency})
                        </span>
                      )}
                    </div>
                    <div className="wallet-history-item-date">
                      {gwLabel} · {new Date(deposit.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="wallet-history-item-right">
                  <span
                    className="wallet-history-status-pill"
                    style={{
                      background: meta.bg,
                      color: meta.color,
                      border: `1px solid ${meta.color}44`
                    }}
                  >
                    {meta.label}
                  </span>
                  {(deposit.gateway === 'swychr' || deposit.gateway === 'notchpay') && deposit.status === 'pending' && (
                    <button
                      onClick={() => handleVerifyDeposit(deposit.id, deposit.gateway)}
                      disabled={verifyingId === deposit.id}
                      className="wallet-history-verify-btn"
                    >
                      {verifyingId === deposit.id ? (
                        <>
                          <span className="spinner" style={{ width: 10, height: 10, borderWidth: 1, borderTopColor: 'var(--accent)', marginRight: 2 }} />
                          Checking...
                        </>
                      ) : (
                        'Verify'
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Guide steps card */}
      <div className="wallet-steps-card">
        <div className="wallet-steps-header">
          <Activity size={15} style={{ color: 'var(--accent)' }} /> How deposits work
        </div>
        <div className="wallet-step-row">
          <span className="wallet-step-num">1</span>
          <span>{gatewayInfo.depositSteps[0]}</span>
        </div>
        <div className="wallet-step-row">
          <span className="wallet-step-num">2</span>
          <span>{gatewayInfo.depositSteps[1]}</span>
        </div>
        <div className="wallet-step-row">
          <span className="wallet-step-num">3</span>
          <span>{gatewayInfo.depositSteps[2]}</span>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-dim)' }}>
          🔒 Your wallet balance is automatically credited in real-time once verified.
        </div>
      </div>
    </div>
  );

  const handleVerifyDeposit = async (depositId) => {
    setVerifyingId(depositId);
    setError('');
    setSuccess('');
    try {
      const res = await depositsAPI.verifySwychr(depositId);
      if (res.data?.status === 'completed') {
        setSuccess('Swychr deposit verified and credited successfully!');
        refreshUser();
        depositsAPI.history().then((r) => setHistory(r.data.deposits || []));
      } else {
        setError(res.data?.message || 'Payment is still pending on Swychr.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Swychr verification failed.');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!availableGateways.length) {
      setError('No payment gateway is currently enabled.');
      return;
    }

    if (isCryptoMinEnforced && accountAmountUsd < cryptoMinUsd) {
      setError(`Minimum crypto deposit is $${cryptoMinUsd.toFixed(2)} USD`);
      return;
    }

    if (isMinDepositEnforced && (!amountNum || amountNum < minimumDepositInInputCurrency)) {
      setError(isMultiCountry ? minimumDepositLabel : `Minimum deposit is ${formatCurrencyWithConversion(minimumDepositUsd, userCurrency, quickChipDecimals)}`);
      return;
    }
    if (!amountNum || amountNum <= 0) {
      setError('Please enter a valid deposit amount.');
      return;
    }

    setLoading(true);

    // Direct Pay gateways (Fapshi & Swychr Direct) — navigate to checkout page which handles the phone input & USSD push
    if (isFapshi || isSwychr) {
      try {
        navigate('/dashboard/deposit/checkout', {
          state: {
            gateway: isSwychr ? 'Mobile Money Africa' : 'Mobile Money',
            gatewayId: gateway,
            isDirectPay: true,
            selectedMarket: isSwychr ? selectedSwychrMarket : null,
            countryCode: isSwychr ? (selectedSwychrMarket?.countryCode || 'CM') : 'CM',
            currency: isSwychr ? (selectedSwychrMarket?.currency || 'XAF') : 'XAF',
            localAmount: isSwychr ? swychrLocalAmount : Math.max(100, mobileGatewayAmountXaf),
            xafAmount: isSwychr ? swychrLocalAmount : Math.max(100, mobileGatewayAmountXaf),
            creditedUsd: accountAmountUsd,
            displayAmount: isSwychr
              ? formatCurrency(swychrLocalAmount, selectedSwychrMarket?.currency || 'XAF', swychrLocalDecimals)
              : formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals),
          },
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      let res;

      if (isNotchpay) {
        if (!selectedSwychrMarket) {
          setError('No market is available right now.');
          return;
        }
        res = await depositsAPI.initiateNotchpay({
          amount_local: swychrLocalAmount,
          country_code: selectedSwychrMarket.countryCode,
          currency: selectedSwychrMarket.currency,
        });
      } else if (isMonetbil) {
        res = await depositsAPI.initiateMonetbil(Math.max(100, mobileGatewayAmountXaf));
      } else if (isSwychrCrypto) {
        res = await depositsAPI.initiateSwychrCrypto({
          amount: accountAmountUsd,
          chain: cryptoChain || 'TRON',
        });
      } else {
        res = await depositsAPI.initiateHelaket(accountAmountUsd, currency);
      }

      const payUrl = res.data.payment_url;
      if (!payUrl) {
        setError('Payment gateway returned no redirect URL. Contact support.');
        return;
      }

      const creditedUsd = isMobileMoneyGateway ? res.data.usd_amount : accountAmountUsd;
      const gwName = isNotchpay ? 'Mobile Money Africa 2' : isMonetbil ? 'Monetbil' : isSwychrCrypto ? 'Crypto' : 'Helaket Crypto';
      if (res.data.deposit_id) {
        if (isMonetbil) {
          sessionStorage.setItem(MONETBIL_PENDING_DEPOSIT_STORAGE_KEY, String(res.data.deposit_id));
        } else if (isSwychrCrypto) {
          sessionStorage.setItem('nb_swychr_crypto_pending_deposit_id', String(res.data.deposit_id));
        }
      }
      navigate('/dashboard/deposit/checkout', {
        state: {
          gateway: gwName,
          gatewayId: gateway,
          paymentUrl: payUrl,
          creditedUsd: parseFloat(creditedUsd || 0),
          displayAmount: isSwychrCrypto
            ? `$${parseFloat(accountAmountUsd).toFixed(2)} USD (${res.data.amount_crypto || accountAmountUsd} USDT - ${res.data.chain || cryptoChain})`
            : formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals),
        },
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(msg || 'Payment initiation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(inputCurrency);
  const presetAmounts = isZeroDecimal
    ? ['1000', '2000', '5000', '10000', '25000', '50000']
    : ['5', '10', '25', '50', '100', '200'];

  return (
    <div className="wallet-page-container">
      {/* Page Header */}
      <div>
        <h1 className="page-title">{t('wallet.title', 'Wallet')}</h1>
        <p className="page-subtitle">{t('wallet.subtitle', 'Add funds to your wallet to place orders easily.')}</p>
      </div>

      {/* Essential Balance Card */}
      <div className="wallet-hero-card">
        <div>
          <div className="wallet-hero-title-wrap" style={{ marginBottom: 6 }}>
            <span className="wallet-hero-label">{t('wallet.current_balance', 'Available Balance')}</span>
            <span className="wallet-currency-badge">{userCurrency}</span>
          </div>
          <div className="wallet-hero-balance">{balanceDisplay}</div>
        </div>

        <button
          type="button"
          onClick={() => refreshUser({ force: true })}
          className="btn btn-sm"
          style={{
            background: 'rgba(255, 255, 255, 0.16)',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.28)',
            borderRadius: 12,
            backdropFilter: 'blur(8px)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            padding: '9px 16px',
            fontWeight: 700,
            fontSize: 13
          }}
          title="Refresh wallet balance"
        >
          <RefreshCw size={14} /> {t('common.refresh', 'Refresh')}
        </button>
      </div>

      {/* Bonus Banner */}
      {firstDepositBonus?.first_deposit_enabled &&
        firstDepositBonus?.first_deposit_eligible &&
        Number(firstDepositBonus.first_deposit_percent || 0) > 0 && (
        <div className="wallet-bonus-banner">
          <div className="wallet-bonus-icon">🎁</div>
          <div>
            <div className="wallet-bonus-title">
              First Deposit Bonus Activated!
            </div>
            <div className="wallet-bonus-desc">
              Get an extra <strong>+{firstDepositBonus.first_deposit_percent}% bonus</strong> instantly added to your wallet on your first deposit!
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Main 2-column layout */}
      <div className="wallet-layout-grid">
        {/* Left Column: Gateways & Deposit Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* 1. Payment Methods Selection */}
          <div className="wallet-card">
            <div className="wallet-section-title">
              <CreditCard size={18} style={{ color: 'var(--accent)' }} /> {t('wallet.select_payment_method', '1. Select Payment Method')}
            </div>

            <div className="wallet-gateways-grid">
              {availableGateways.map((gw) => {
                const isActive = gateway === gw.id;
                return (
                  <div
                    key={gw.id}
                    onClick={() => switchGateway(gw.id)}
                    className={`wallet-gateway-card ${isActive ? 'active' : ''}`}
                    style={{
                      borderColor: isActive ? 'var(--accent)' : undefined,
                      boxShadow: isActive ? '0 0 20px rgba(37, 99, 235, 0.22)' : undefined
                    }}
                  >
                    <div className="wallet-gw-header">
                      <div
                        className="wallet-gw-icon-box"
                        style={{
                          background: 'rgba(37, 99, 235, 0.12)',
                          color: 'var(--accent)'
                        }}
                      >
                        {gw.icon}
                      </div>

                      <div className="wallet-gw-meta">
                        <div className="wallet-gw-name">
                          <span>{gw.name}</span>
                          {gw.badge && (
                            <span
                              className="wallet-gw-badge"
                              style={{
                                background: 'rgba(37, 99, 235, 0.12)',
                                color: 'var(--accent)',
                                border: '1px solid rgba(37, 99, 235, 0.25)'
                              }}
                            >
                              {gw.badge}
                            </span>
                          )}
                        </div>
                        <div className="wallet-gw-sub" style={{ color: 'var(--accent)' }}>
                          {gw.sub}
                        </div>
                      </div>

                      {isActive && (
                        <div className="wallet-gw-check" style={{ background: 'var(--accent)' }}>
                          ✓
                        </div>
                      )}
                    </div>

                    <div className="wallet-gw-desc">{gw.desc}</div>
                  </div>
                );
              })}
            </div>

            {!availableGateways.length && (
              <div className="alert alert-error">
                All payment gateways are currently disabled by administrator.
              </div>
            )}
          </div>

          {/* 2. Form details card */}
          <div className="wallet-card">
            <div className="wallet-section-title">
              <Zap size={18} style={{ color: 'var(--accent)' }} /> 2. Enter Deposit Amount
            </div>

            <form onSubmit={handleDeposit}>
              {/* Multi-Country selector for Africa Mobile Money */}
              {isMultiCountry && (
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label" style={{ fontWeight: 700 }}>Select Country & Currency</label>
                  <select
                    className="form-input"
                    value={swychrMarketCode}
                    onChange={(e) => {
                      setSwychrMarketCode(e.target.value);
                      setAmount('');
                    }}
                    style={{ fontSize: 14, fontWeight: 600 }}
                  >
                    {swychrMarkets.map((market) => (
                      <option
                        key={`${market.countryCode}:${market.currency}`}
                        value={`${market.countryCode}:${market.currency}`}
                      >
                        {market.flag || ''} {market.label} ({market.currency})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* USDT Crypto Network Selector */}
              {isSwychrCrypto && (
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label" style={{ fontWeight: 700 }}>Select USDT Transfer Network</label>
                  <div className="wallet-networks-row">
                    {[
                      { id: 'TRON', label: 'TRON (TRC-20)' },
                      { id: 'BSC', label: 'BNB Chain (BEP-20)' },
                      { id: 'ETH', label: 'Ethereum (ERC-20)' },
                    ].map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setCryptoChain(item.id)}
                        className={`wallet-network-btn ${cryptoChain === item.id ? 'active' : ''}`}
                      >
                        {cryptoChain === item.id && <span>●</span>}
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Helaket multi-coin selector */}
              {isHelaket && (
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label" style={{ fontWeight: 700 }}>Select Cryptocurrency</label>
                  <div className="wallet-networks-row">
                    {['USDT', 'BTC', 'ETH', 'LTC'].map((coin) => (
                      <button
                        key={coin}
                        type="button"
                        onClick={() => setCurrency(coin)}
                        className={`wallet-network-btn ${currency === coin ? 'active' : ''}`}
                      >
                        {currency === coin && <span>●</span>}
                        {coin}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Preset Amount Chips */}
              <div className="wallet-presets-container">
                <div className="wallet-presets-label">
                  <span>{t('wallet.quick_amounts', 'Quick Amounts')}</span>
                  <span>{t('wallet.one_tap_fill', '1-Tap Fill')}</span>
                </div>
                <div className="wallet-presets-grid">
                  {presetAmounts.map((presetVal) => {
                    const isSelected = String(amount) === String(presetVal);
                    return (
                      <button
                        key={presetVal}
                        type="button"
                        onClick={() => setAmount(presetVal)}
                        className={`wallet-preset-chip ${isSelected ? 'active' : ''}`}
                      >
                        {Number(presetVal).toLocaleString()} {inputCurrency}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Amount Input */}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Amount in {inputCurrency}</span>
                  {minimumDepositLabel && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
                      Min: {minimumDepositLabel}
                    </span>
                  )}
                </label>

                <div className="wallet-input-wrap">
                  <span className="wallet-input-prefix">
                    {inputCurrencyInfo?.symbol || inputCurrency}
                  </span>
                  <input
                    className="wallet-amount-input"
                    type="number"
                    step={inputDecimals === 0 ? '1' : '0.01'}
                    min={String(minimumDepositInInputCurrency.toFixed(inputDecimals === 0 ? 0 : 2))}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={isZeroDecimal ? 'e.g. 5000' : 'e.g. 25.00'}
                    required
                  />
                </div>
              </div>

              {/* Live breakdown summary box */}
              {amountNum > 0 && (
                <div className="wallet-summary-card">
                  <div className="wallet-summary-row">
                    <span>{t('wallet.depositing', 'You are depositing')}</span>
                    <strong style={{ color: 'var(--text)' }}>
                      {formatCurrency(amountNum, inputCurrency, inputDecimals)}
                    </strong>
                  </div>
                  <div className="wallet-summary-row">
                    <span>{t('wallet.credited_to_wallet', 'Credited to Wallet')}</span>
                    <strong style={{ color: 'var(--green-500)', fontSize: 15 }}>
                      {formatCurrencyWithConversion(accountAmountUsd, userCurrency, ZERO_DECIMAL_CURRENCIES.has(userCurrency) ? 0 : 2)}
                    </strong>
                  </div>
                  <div className="wallet-summary-row total">
                    <span>Settlement</span>
                    <span style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Zap size={13} /> Instant Automatic Credit
                    </span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !amount || amountNum <= 0}
                className="wallet-submit-btn"
                style={{
                  background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                  boxShadow: loading ? 'none' : '0 8px 24px rgba(37, 99, 235, 0.35)'
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2, borderTopColor: '#fff', marginRight: 6 }} />
                    Preparing Payment...
                  </>
                ) : isFapshi ? (
                  <>
                    {t('wallet.pay_now', 'Pay')} {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} <ArrowRight size={16} />
                  </>
                ) : isSwychr ? (
                  <>
                    {t('wallet.pay_now', 'Pay')} {amountNum ? formatCurrency(amountNum, inputCurrency, inputDecimals) : ''} <ArrowRight size={16} />
                  </>
                ) : isSwychrCrypto ? (
                  <>
                    {t('wallet.pay_now', 'Pay')} {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} (USDT) <ArrowRight size={16} />
                  </>
                ) : isNotchpay ? (
                  <>
                    {t('wallet.pay_now', 'Pay')} {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} <ArrowRight size={16} />
                  </>
                ) : isMonetbil ? (
                  <>
                    {t('wallet.pay_now', 'Pay')} {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} <ArrowRight size={16} />
                  </>
                ) : (
                  <>
                    {t('wallet.pay_now', 'Pay')} {amountNum ? formatCurrencyWithConversion(accountAmountUsd, userCurrency, quickChipDecimals) : ''} <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Right Column: Recent Activity & Instructions */}
        <div>
          {renderHistoryCard()}
        </div>
      </div>
    </div>
  );
}
